import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { USER } from "@/constants/testIds";
import DocumentationPanel from "@/components/jobs/DocumentationPanel";
import ParametersStep, { getParameterErrors, getDefaultParameterValues } from "@/pages/jobs/parameters";
import { ParameterReviewTable, RawDataSourceReview } from "@/components/jobs/ParameterReviewTable";

const STEPS = ["Module", "Metadata", "Raw Data Source", "Parameters", "Review"];

const SAMPLE_REPORTS = {
  "chip-seq": "/sample-reports/chipseq.html",
  "whole-exome-sequencing": "/sample-reports/whole-exome-sequencing.html",
  "metagenome": "/sample-reports/metagenome.html",
  "raw-data-qc": "/sample-reports/raw-data-qc.html",
  "transcriptome-resequencing": "/sample-reports/transcriptome-resequencing.html",
  "variant-analysis": "/sample-reports/variant-analysis.html",
};

export default function JobCreatePage() {
  const [modules, setModules] = useState(null);
  const [balance, setBalance] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const [form, setForm] = useState({
    module_key: "", project_name: "", short_notes: "",
    number_of_samples: 1, raw_data_source: {
      type: "online", // online | offline
      link: "",
      username: "",
      password: "",
    }, metadata: {}, parameters: {},
  });

  useEffect(() => {
    Promise.all([api.get("/modules"), api.get("/credits/balance")]).then(([m, b]) => {
      setModules(m.data);
      setBalance(b.data);
    });
  }, []);

  const module = useMemo(
    () => (modules || []).find((m) => m.key === form.module_key),
    [modules, form.module_key],
  );
  const requiredCredits = module ? module.credits_per_sample * (Number(form.number_of_samples) || 0) : 0;
  const insufficient = balance && requiredCredits > balance.available;
  const parameterErrors = useMemo(() => getParameterErrors(form.module_key, form.parameters), [form.module_key, form.parameters]);

  const setSamples = (n) => {
    const N = Math.max(1, Math.min(1000, Number(n) || 1));
    setForm((prev) => ({ ...prev, number_of_samples: N }));
  };
  const selectModule = (moduleKey) => {
    setForm((prev) => ({
      ...prev,
      module_key: moduleKey,
      parameters: getDefaultParameterValues(moduleKey),
    }));
  };
  const updateParameter = (key, value) => {
    setForm((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [key]: value },
    }));
  };

  const canNext = () => {
    if (step === 0) return !!form.module_key;
    if (step === 1) return !!form.project_name.trim();
    if (step === 2) {
      if (form.raw_data_source.type === "offline") {
        return true;
      }

      return (
        !!form.raw_data_source.link &&
        /^(https?|ftp):\/\//i.test(form.raw_data_source.link.trim())
      );
    }
    if (step === 3) {
      return Object.keys(parameterErrors).length === 0;
    }
    return true;
  };

  const submit = async () => {
    if (insufficient) return toast.error("Insufficient credits — please purchase more first.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        ...form,
        parameters: form.parameters,
        raw_data_source: {
          ...form.raw_data_source,
          link: form.raw_data_source.link.trim(),
        },
      });
      toast.success(`Job ${data.job_code} submitted for review.`);
      nav(`/app/jobs/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (!modules || !balance) return <LoadingScreen />;

  return (
    <div className="max-w-[1400px]">

      <div className="grid xl:grid-cols-[1fr_340px] gap-6">

        {/* LEFT SIDE */}
        <div>
          <ol className="flex items-center gap-2 mb-8">
            {STEPS.map((label, i) => (
              <li key={label} className="flex items-center gap-2 flex-1">
                <div className={`flex items-center gap-2 ${i <= step ? "text-[#0B6936]" : "text-gray-400"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${i < step ? "bg-[#0B6936] border-[#0B6936] text-white"
                    : i === step ? "border-[#0B6936] bg-[#EAF4E3] text-[#0B6936]"
                      : "border-gray-300 bg-white"}`}>
                    {i < step ? <CheckCircle2 size={14} /> : i + 1}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`w-16 h-px ${i < step ? "bg-[#0B6936]" : "bg-gray-200"
                      }`}
                  />
                )}
              </li>
            ))}
          </ol>

          <div className="sqit-card p-8">
            {step > 0 && (
              <div className="mb-6">
                <h2 className="font-display text-xl font-semibold text-gray-900">
                  {STEPS[step]}
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  {step === 1 && "Provide your project information."}
                  {step === 2 && "Provide the sample data link."}
                  {step === 3 && "Configure the analysis parameters."}
                  {step === 4 && "Review all information before submitting."}
                </p>
              </div>
            )}
            {step === 0 && (
              <div>
                <h2 className="font-display text-xl font-semibold text-gray-900">Select analysis module</h2>
                <p className="text-sm text-gray-500 mt-1">Each module is billed per sample.</p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={USER.wizardModuleSelect}>
                  {modules
                    .filter((m) => m.display_in?.jobs ?? true)
                    .map((m) => (
                      <div
                        key={m.key}
                        onClick={() => selectModule(m.key)}
                        data-testid={`wizard-module-${m.key}`}
                        className={`cursor-pointer text-left p-4 rounded-lg border-2 transition-colors ${form.module_key === m.key
                          ? "border-[#0B6936] bg-[#EAF4E3]"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-display text-lg font-semibold text-gray-900">
                              {m.name}
                            </div>

                            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                              {m.tagline}
                            </p>
                          </div>

                          {m.credits_per_sample > 1 ? (
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-500 line-through decoration-2 decoration-black-500">
                                {m.credits_per_sample} credits
                              </span>

                              <span className="rounded-full bg-[#EAF4E3] px-2 py-0.5 text-xs font-bold text-[#0B6936] border border-[#0B6936]">
                                1 credit
                              </span>

                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600 border border-red-200">
                                80% OFF
                              </span>
                            </div>
                          ) : (
                            <span className="rounded-full bg-[#EAF4E3] px-2 py-0.5 text-xs font-bold text-[#0B6936] border border-[#0B6936]">
                              1 credit
                            </span>
                          )}
                        </div>

                      </div>
                    ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>

                <div className="mt-6 grid gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Project name *</label>
                    <input
                      data-testid={USER.wizardProjectName}
                      value={form.project_name}
                      onChange={(e) => setForm((prev) => ({ ...prev, project_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#0B6936]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Number of samples *</label>
                    <input
                      data-testid={USER.wizardNumSamples}
                      type="number" min={1} max={1000}
                      value={form.number_of_samples}
                      onChange={(e) => setSamples(e.target.value)}
                      className="w-32 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#0B6936]"
                    />
                    {module && (
                      <p className="text-xs text-gray-500 mt-2">
                        Will freeze <strong className="text-[#0B6936]">{requiredCredits} credits</strong> from your available balance
                        ({balance.available} available).
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Short notes</label>
                    <textarea
                      data-testid={USER.wizardShortNotes}
                      rows={4}
                      value={form.short_notes}
                      onChange={(e) => setForm((prev) => ({ ...prev, short_notes: e.target.value }))}
                      placeholder="Any context our team should know…"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#0B6936]"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">

                {/* Source Type */}
                <div>

                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={form.raw_data_source.type === "online"}
                        onChange={() =>
                          setForm(prev => ({
                            ...prev,
                            raw_data_source: {
                              ...prev.raw_data_source,
                              type: "online",
                            },
                          }))
                        }
                      />
                      <span>Online</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={form.raw_data_source.type === "offline"}
                        onChange={() =>
                          setForm(prev => ({
                            ...prev,
                            raw_data_source: {
                              ...prev.raw_data_source,
                              type: "offline",
                            },
                          }))
                        }
                      />
                      <span>Offline</span>
                    </label>
                  </div>
                </div>

                {/* Online Fields */}
                {form.raw_data_source.type === "online" && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Data Link *
                      </label>

                      <input
                        value={form.raw_data_source.link}
                        onChange={(e) =>
                          setForm(prev => ({
                            ...prev,
                            raw_data_source: {
                              ...prev.raw_data_source,
                              link: e.target.value,
                            },
                          }))
                        }
                        placeholder="https://drive.google.com/..."
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#0B6936]"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Username / Email (Optional)
                        </label>

                        <input
                          value={form.raw_data_source.username}
                          onChange={(e) =>
                            setForm(prev => ({
                              ...prev,
                              raw_data_source: {
                                ...prev.raw_data_source,
                                username: e.target.value,
                              },
                            }))
                          }
                          placeholder="username@example.com"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Password (Optional)
                        </label>

                        <input
                          type="password"
                          value={form.raw_data_source.password}
                          onChange={(e) =>
                            setForm(prev => ({
                              ...prev,
                              raw_data_source: {
                                ...prev.raw_data_source,
                                password: e.target.value,
                              },
                            }))
                          }
                          placeholder="Only if required"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>

                    </div>
                  </>
                )}

                {/* Offline Note */}
                {form.raw_data_source.type === "offline" && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    You have selected <strong>Offline</strong> as the data source.
                    <br />
                    Our team will contact you to coordinate the data transfer process and provide further instructions.
                  </div>
                )}

              </div>
            )}

            {step === 3 && (
              <div>
                <p className="text-sm text-gray-500 mt-1">The form below adapts to the selected module and contains only analysis settings.</p>
                <div className="mt-6">
                  <ParametersStep
                    moduleKey={form.module_key}
                    values={form.parameters}
                    onChange={updateParameter}
                    errors={parameterErrors}
                    expectedSamples={form.number_of_samples}
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <dl className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">Module</dt>
                    <dd className="font-semibold text-gray-900 mt-0.5">{module?.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">Project</dt>
                    <dd className="font-semibold text-gray-900 mt-0.5">{form.project_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">Samples</dt>
                    <dd className="font-semibold text-gray-900 mt-0.5">{form.number_of_samples}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">Credits required</dt>
                    <dd className={`font-semibold mt-0.5 ${insufficient ? "text-[#DC2626]" : "text-[#0B6936]"}`}>
                      {requiredCredits} credits {insufficient && "(insufficient)"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">
                      Raw Data Source
                    </dt>

                    <dd className="mt-2"><RawDataSourceReview source={form.raw_data_source} /></dd>
                  </div>
                  <div className="md:col-span-2">
                    <dt className="text-xs text-gray-500 uppercase tracking-wider">Parameters</dt>
                    <dd className="mt-2"><ParameterReviewTable parameters={form.parameters} /></dd>
                  </div>
                </dl>
                {insufficient && (
                  <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                    You need {requiredCredits - balance.available} more credits. <a href="/app/credits" className="font-semibold underline">Purchase credits</a>.
                  </div>
                )}
                {form.short_notes && (
                  <div className="mt-6">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Notes</div>
                    <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{form.short_notes}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between pt-6 border-t border-[#E5E7EB]">
              <button
                disabled={step === 0}
                onClick={() => setStep(step - 1)}
                data-testid={USER.wizardPrev}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40"
              >
                <ChevronLeft size={14} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  disabled={!canNext()}
                  onClick={() => setStep(step + 1)}
                  data-testid={USER.wizardNext}
                  className="inline-flex items-center gap-1.5 bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-40"
                >
                  Continue <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  disabled={submitting || insufficient}
                  onClick={submit}
                  data-testid={USER.wizardSubmit}
                  className="bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-40"
                >
                  {submitting ? "Submitting…" : "Submit job"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <aside className="sticky top-6 self-start w-full">
          {step === 0 ? (
            <div className="rounded-xl border border-[#D7E7D1] bg-[#F8FBF7] shadow-sm overflow-hidden">

              {/* Header */}
              <div className="bg-[#0B6936] px-5 py-4 text-white">
                <h3 className="text-lg font-semibold">
                  Standardized Analysis Reports
                </h3>
                <p className="mt-1 text-sm text-green-100">
                  Every analysis performed using SQIT follows a validated,
                  predefined reporting workflow.
                </p>
              </div>

              {/* Content */}
              <div className="p-5 space-y-5">

                <div className="flex gap-3">
                  <div className="mt-1 text-green-700 text-lg">✔</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Publication-ready Reports
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Reports are generated using standardized formats suitable for
                      research publications and scientific documentation.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 text-green-700 text-lg">✔</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Fixed Report Structure
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Every project follows the same predefined report layout to
                      ensure consistency across analyses.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="mt-1 text-green-700 text-lg">✔</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Quality Metrics Included
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Reports include analysis summaries, quality statistics,
                      figures, tables, and interpretation where applicable.
                    </p>
                  </div>
                </div>


                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h4 className="font-semibold text-amber-900">
                    Important Note
                  </h4>

                  <p className="mt-2 text-sm text-amber-800 leading-6">
                    The sample reports available for each module accurately represent
                    the report format you will receive after successful analysis.
                    Report layouts are standardized and cannot be customized on a
                    per-project basis.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => nav('/app/sample-reports/')}
                  className="w-full rounded-lg border border-[#0B6936] bg-white py-3 text-sm font-semibold text-[#0B6936] hover:bg-[#0B6936] hover:text-white"
                >
                  View Sample Report
                </button>

              </div>
            </div>

          ) : (
            <DocumentationPanel
              moduleKey={form.module_key}
              step={step}
            />
          )}
        </aside>

      </div>
    </div>
  )
};
